/**
 * Entity-withheld denials — the wire contract and the transport-neutral
 * recovery for it.
 *
 * A federated representation the viewer may not read resolves as a key-only
 * stub with null content, and the accompanying object-level error carries
 * `extensions.code: FORBIDDEN` (see the OrganizationalUnit denial shape in
 * identity-api's SDL). The router rewrites the subgraph's `_entities[i]` path
 * to the representation's own place in the response, so the defining property
 * of such an error is that **the object at its path still exists in `data`** —
 * the stub. That is what tells it apart from a whole-operation denial, which
 * shares the code but nulls the field it points at. Consumers keep the partial
 * data, treat null content as the denial, and filter these errors out of their
 * fatal-error handling; everything else stays loud.
 *
 * `FORBIDDEN` is deliberately NOT an `ErrorCode` enum member: that enum
 * classifies whole-operation failures (the route boundary maps UNAUTHORISED
 * straight to a 403 page), while this code marks one object inside an
 * otherwise successful response.
 *
 * This module is the package's `./entity-withheld` entry: pure and
 * dependency-free so route loaders and their tests can import it without
 * evaluating the component barrel.
 */
export const ENTITY_WITHHELD_ERROR_CODE = "FORBIDDEN";

/** The shape of a GraphQL error as the predicate sees it — structural, so it
 * fits both Apollo's `GraphQLFormattedError` and graphql-request's envelope;
 * only `path` and `extensions.code` are read. */
export type WithheldCandidateError = {
  message?: string;
  locations?: unknown;
  path?: ReadonlyArray<string | number> | null;
  extensions?: { code?: unknown } | null;
} | null;

const valueAtPath = (data: unknown, path: ReadonlyArray<string | number>): unknown => {
  let current: unknown = data;
  for (const segment of path) {
    if (typeof current !== "object" || current === null) {
      return undefined;
    }
    current = (current as Record<string | number, unknown>)[segment];
  }
  return current;
};

/**
 * True when the error is an entity-withheld denial against `data`: it carries
 * the withheld code AND its path resolves to a non-null object — the stub the
 * subgraph served in place of the content. A FORBIDDEN whose path is absent or
 * points at null is a whole-operation denial and is NOT withheld.
 */
export function isEntityWithheldError(error: WithheldCandidateError, data: unknown): boolean {
  if (!error || error.extensions?.code !== ENTITY_WITHHELD_ERROR_CODE) {
    return false;
  }
  if (!Array.isArray(error.path) || error.path.length === 0) {
    return false;
  }
  const target = valueAtPath(data, error.path);
  return typeof target === "object" && target !== null;
}

type GraphQLResponseShapedError = {
  response?: {
    data?: unknown;
    errors?: ReadonlyArray<WithheldCandidateError> | null;
  } | null;
};

/**
 * Returns the partial data carried by a rejected graphql-request envelope
 * whose errors are all withheld-entity denials against that very data, or
 * undefined when the rejection is anything else (a whole-operation denial,
 * mixed codes, no data, not a GraphQL envelope).
 */
function recoverWithheldPartialData<TData>(error: unknown): TData | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const response = (error as GraphQLResponseShapedError).response;
  const data = response?.data;
  if (data == null) {
    return undefined;
  }

  const errors = response?.errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return undefined;
  }
  if (!errors.every((entry) => isEntityWithheldError(entry, data))) {
    return undefined;
  }

  return data as TData;
}

/**
 * Runs a graphql-request read, recovering the partial response when the only
 * errors are withheld-entity denials (graphql-request rejects any data+errors
 * envelope outright, which would let the transport pre-empt the caller's own
 * denial handling). The caller then owns the denial: null content on the
 * recovered data means WITHHELD. Anything else rethrows untouched.
 */
export async function requestRecoveringWithheldEntities<TData>(
  run: () => Promise<TData>,
): Promise<TData> {
  try {
    return await run();
  } catch (error) {
    const partial = recoverWithheldPartialData<TData>(error);
    if (partial !== undefined) {
      return partial;
    }
    throw error;
  }
}
